// =============================================================================
// app/src/lib/sistema.ts — as regras da aba Sistema, longe do JSX.
//
// POR QUE O LIMIAR MORA AQUI E NÃO NO SQL
// `saude_sistema()` devolve FATO — 6,1 GB de 96 GB usados, último backup às
// 03:00. Dizer que 6,1 GB está "ok" e que um backup de 50 horas está "crítico"
// é regra de produto: muda quando o e-commerce da Fase 4 entrar, muda se o
// backup passar a ser de hora em hora. Regra que muda precisa de teste, e teste
// de limiar em PL/pgSQL exige banco de pé; aqui é uma função pura.
//
// A separação também impede o erro clássico de tela de monitoramento: pintar de
// vermelho o que é normal. Memória "quase cheia" no Linux é saudável (o kernel
// usa a RAM livre como cache), e é por isso que o agente reporta MemAvailable —
// ver o comentário em infra/agente/servidor.mjs.
// =============================================================================

/** Ordem importa: `pior()` depende dela. */
export const NIVEIS = ['ok', 'atencao', 'critico'] as const;
export type Nivel = (typeof NIVEIS)[number];

/** O nível mais grave de um conjunto — o que define a cor do topo da tela. */
export function pior(niveis: Nivel[]): Nivel {
  return niveis.reduce<Nivel>(
    (acc, n) => (NIVEIS.indexOf(n) > NIVEIS.indexOf(acc) ? n : acc),
    'ok',
  );
}

// --- Recursos da máquina ------------------------------------------------------

/**
 * Uso de disco. 80/90 e não 90/95: o alerta precisa chegar com dias de
 * antecedência, porque a correção (limpar imagem velha do Docker, mover dump
 * para fora) leva tempo e ninguém quer fazer isso com o disco em 99% e o
 * Postgres já se recusando a escrever.
 */
export function nivelDoDisco(usadoGb: number | null, totalGb: number | null): Nivel {
  if (!usadoGb || !totalGb || totalGb <= 0) return 'ok';
  const pct = (usadoGb / totalGb) * 100;
  if (pct >= 90) return 'critico';
  if (pct >= 80) return 'atencao';
  return 'ok';
}

/**
 * Memória. O agente manda "usada" já descontando cache (MemAvailable), então
 * 85% aqui significa 85% de memória de verdade indisponível — com
 * shared_buffers=2GB no Postgres, é a hora de olhar antes que o kernel comece
 * a matar processo.
 */
export function nivelDaMemoria(usadaMb: number | null, totalMb: number | null): Nivel {
  if (!usadaMb || !totalMb || totalMb <= 0) return 'ok';
  const pct = (usadaMb / totalMb) * 100;
  if (pct >= 95) return 'critico';
  if (pct >= 85) return 'atencao';
  return 'ok';
}

/**
 * CPU. Limiar alto de propósito: um pico de 90% durante um build é o sistema
 * funcionando, não falhando. O sinal que importa de verdade é a carga
 * sustentada, e para isso existe o gráfico — não o semáforo.
 */
export function nivelDaCpu(pct: number | null): Nivel {
  if (pct === null || Number.isNaN(pct)) return 'ok';
  if (pct >= 95) return 'critico';
  if (pct >= 85) return 'atencao';
  return 'ok';
}

// --- Backup -------------------------------------------------------------------

export const BACKUP_ATENCAO_H = 26; // diário com folga de 2 h
export const BACKUP_CRITICO_H = 48;

/**
 * Idade do último dump. `null` é CRÍTICO, nunca "sem informação": foi
 * exatamente esse o estado da VPS em 2026-08-06 — nenhum backup, nenhum
 * agendamento, e ninguém sabia. Uma tela que mostrasse "—" ali teria deixado o
 * problema invisível por mais um mês.
 */
export function nivelDoBackup(backupEm: string | null, agora: number): Nivel {
  if (!backupEm) return 'critico';
  const horas = (agora - Date.parse(backupEm)) / 3_600_000;
  if (!Number.isFinite(horas) || horas >= BACKUP_CRITICO_H) return 'critico';
  if (horas >= BACKUP_ATENCAO_H) return 'atencao';
  return 'ok';
}

export function textoDoBackup(backupEm: string | null, agora: number): string {
  if (!backupEm) return 'NUNCA houve backup deste banco';
  return `último backup ${formatarIdade(backupEm, agora)}`;
}

// --- Containers ---------------------------------------------------------------

export interface ContainerLido {
  nome: string;
  estado: string;
  saude: string | null;
}

export interface ResumoContainers {
  total: number;
  rodando: number;
  /** Nome dos que não estão de pé ou estão marcados como unhealthy. */
  problemas: string[];
  nivel: Nivel;
}

/**
 * Um container fora do ar é sempre crítico: são quatro, e cada um derruba uma
 * parte inteira do sistema (banco, login, dados, o site). Não existe "meio
 * grave" aqui — se o gotrue caiu, ninguém entra.
 */
export function resumirContainers(lista: ContainerLido[] | null): ResumoContainers {
  const itens = lista ?? [];
  const problemas = itens
    .filter((c) => c.estado !== 'running' || c.saude === 'unhealthy')
    .map((c) => c.nome);
  return {
    total: itens.length,
    rodando: itens.filter((c) => c.estado === 'running').length,
    problemas,
    // Lista vazia = o agente não conseguiu falar com o Docker. Isso é ruído,
    // não incêndio: ou o agente acabou de subir, ou o docker está reiniciando.
    nivel: itens.length === 0 ? 'atencao' : (problemas.length ? 'critico' : 'ok'),
  };
}

// --- Tráfego ------------------------------------------------------------------

export function taxaDeErro(acessos: number, erros: number): number {
  if (!acessos) return 0;
  return (erros / acessos) * 100;
}

/**
 * 2% e 10%. Um site sempre tem algum 404 (robô procurando /wp-admin), então
 * zero não é a meta. O que dispara é erro em PROPORÇÃO do que se serve.
 */
export function nivelDaTaxaDeErro(acessos: number, erros: number): Nivel {
  // Poucos acessos fazem a porcentagem mentir: 1 erro em 3 requisições é 33% e
  // não significa nada. Abaixo de 50, a taxa não vale como sinal.
  if (acessos < 50) return 'ok';
  const taxa = taxaDeErro(acessos, erros);
  if (taxa >= 10) return 'critico';
  if (taxa >= 2) return 'atencao';
  return 'ok';
}

// --- Publicação ---------------------------------------------------------------

/**
 * Etapas do deploy.sh, na ordem, para a tela desenhar o progresso.
 *
 * Toda `etapa "..."` que infra/agente/deploy.sh emite tem que estar aqui:
 * `progressoDoDeploy` devolve 0 para chave desconhecida, e a barra que estava
 * em 100% cairia a 0% com a chave crua como rótulo — foi o que "prova" e
 * "voltando" (16/10/2026) fariam se a lista parasse em "schema". O teste em
 * sistema.test.ts lê o deploy.sh e cobra essa correspondência. "iniciando" é
 * do agente, antes do script começar; "voltando" só aparece quando a prova
 * falhou e o conteúdo anterior está voltando para o ar.
 */
export const ETAPAS_DEPLOY = [
  { chave: 'iniciando', rotulo: 'Iniciando' },
  { chave: 'git', rotulo: 'Buscando código' },
  { chave: 'deps', rotulo: 'Dependências' },
  { chave: 'site', rotulo: 'Construindo o site' },
  { chave: 'app', rotulo: 'Construindo o sistema' },
  { chave: 'clientes', rotulo: 'Construindo o portal' },
  { chave: 'conferindo', rotulo: 'Conferindo' },
  { chave: 'publicando', rotulo: 'Publicando' },
  { chave: 'schema', rotulo: 'Avisando o banco' },
  { chave: 'prova', rotulo: 'Provando que está de pé' },
  { chave: 'voltando', rotulo: 'Voltando à versão anterior' },
] as const;

export function progressoDoDeploy(etapa: string | null): number {
  if (!etapa) return 0;
  const i = ETAPAS_DEPLOY.findIndex((e) => e.chave === etapa);
  return i < 0 ? 0 : Math.round(((i + 1) / ETAPAS_DEPLOY.length) * 100);
}

export function rotuloDaEtapa(etapa: string | null): string {
  if (!etapa) return '';
  return ETAPAS_DEPLOY.find((e) => e.chave === etapa)?.rotulo ?? etapa;
}

// --- Cobertura do catálogo ----------------------------------------------------

/** Percentual inteiro, com o caso "total = 0" devolvendo 0 em vez de NaN. */
export function cobertura(parte: number, total: number): number {
  if (!total || total <= 0) return 0;
  return Math.round((parte / total) * 100);
}

// --- Formatação ---------------------------------------------------------------

export function formatarBytes(n: number | null): string {
  if (n === null || !Number.isFinite(n)) return '—';
  if (n < 1024) return `${n} B`;
  const unidades = ['kB', 'MB', 'GB', 'TB'];
  let valor = n / 1024;
  let i = 0;
  while (valor >= 1024 && i < unidades.length - 1) { valor /= 1024; i += 1; }
  return `${valor.toFixed(valor >= 10 ? 0 : 1)} ${unidades[i]}`;
}

export function formatarDuracao(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms) || ms < 0) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)} s`;
  const m = Math.floor(s / 60);
  return `${m} min ${Math.round(s % 60)} s`;
}

/**
 * "há 3 min", "há 2 h", "há 4 dias". Mesmo vocabulário do aviso de dados
 * offline no Layout — quem lê as duas telas não deveria ter que aprender duas
 * formas de dizer a mesma coisa.
 */
export function formatarIdade(iso: string | null, agora: number): string {
  if (!iso) return 'nunca';
  const ms = agora - Date.parse(iso);
  if (!Number.isFinite(ms)) return 'nunca';
  if (ms < 0) return 'agora';
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'agora mesmo';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} h`;
  const d = Math.floor(h / 24);
  return `há ${d} ${d > 1 ? 'dias' : 'dia'}`;
}
